import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import View from "react-native-web/dist/exports/View";
import Text from "react-native-web/dist/exports/Text";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Icon, { iconComponentPropTypes } from './Icon';
import { getStackedContent } from '../StackView';
import { spacingProps } from '../utils';

/**
 * Returns an icon and some text with a sized gap between them. This is a utility component
 * intended for use when creating components, not intended for use in applications on its own.
 *
 * IconText does not wrap its children, so should be used either:
 *  - inline within a <Text> element
 *  - within a container with `flexDirection: 'row'`
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const IconText = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    space,
    iconPosition = 'left',
    icon: IconComponent,
    iconProps,
    children
  } = _ref;
  const iconContent = IconComponent && /*#__PURE__*/_jsx(Icon, {
    ref: ref,
    icon: IconComponent,
    scalesWithText: true,
    ...iconProps
  });

  // Inline images on Android are always baseline-aligned which makes them look misaligned - offset it.
  // See abandoned issue https://github.com/facebook/react-native/issues/6529
  const size = iconProps?.tokens?.size ?? 0;
  const valueTranslateY = iconProps?.tokens?.translateY;
  /**
   * These calculations were carried out using a set of linear equations to calculate that the
   * position of the icon "->"" is aligned to the first line of the tooltip text on IOS and Android.
   * The issue was mainly on IOS, the translateY style didn't match with the old calculations.
   */
  const resultY = valueTranslateY ? Math.floor(-1 * (valueTranslateY - 4)) : 0;
  const iconAdjustedAndroid = /*#__PURE__*/_jsx(View, {
    style: {
      transform: [{
        translateY: valueTranslateY ? size * 0.2 : size * 0.01
      }]
    },
    children: iconContent
  });
  const iconAdjustedIOS = /*#__PURE__*/_jsx(View, {
    style: {
      transform: [{
        translateY: valueTranslateY ? resultY : size * 0.01
      }]
    },
    children: iconContent
  });
  const mobile = Platform.OS === 'android' ? iconAdjustedAndroid : iconAdjustedIOS;
  const adjustedContainer = Platform.OS === 'web' ? iconContent : mobile;
  const adjustedContainerWithPosition = /*#__PURE__*/_jsx(View, {
    style: staticStyles.adjustedContainer,
    children: adjustedContainer
  });
  if (iconPosition === 'inline') {
    return /*#__PURE__*/_jsxs(Text, {
      children: [children, " ", adjustedContainerWithPosition]
    });
  }
  return getStackedContent(iconPosition === 'left' ? [adjustedContainer, children] : [children, adjustedContainer], {
    space,
    direction: 'row'
  });
});
IconText.displayName = 'IconText';
IconText.propTypes = {
  /**
   * Amount of space to separate the text content and icon. Uses the themes's spacing scale
   * (see useSpacingScale for more info).
   */
  space: spacingProps.types.spacingValue,
  /**
   * Whether the icon should be to the left of the content, the right of the content or inline with the content.
   */
  iconPosition: PropTypes.oneOf(['left', 'right', 'inline']),
  /**
   * A valid UDS icon component imported from a UDS palette.
   */
  icon: PropTypes.elementType,
  /**
   * Props that will be passed to the icon component. By default the icon's `scalesWithText`
   * prop will be set as "true" so that the icon continues to match the size of the text
   * if users use accessibility settings to increase text size.
   */
  iconProps: PropTypes.exact(iconComponentPropTypes),
  /**
   * Content to be rendered alongside the Icon component. Usually this should be a
   * `<Typography>` component, or a component that renders `<Text>`.
   */
  children: PropTypes.node
  /* eslint-enable react/no-unused-prop-types */
};
const staticStyles = StyleSheet.create({
  adjustedContainer: {
    position: 'absolute'
  }
});
export default IconText;